/**
 * Durable queue with localStorage persistence and size/TTL caps.
 */
import type { ConsentEvidenceOutboxMarker } from "./consent-evidence";
import type { StorageAdapter } from "./storage";
import type { ConsentSnapshot } from "./types";
import { now } from "./utils";

export type QueuedDeliveryContext = {
  apiBase: string;
  deliveryClass: "essential" | "analytics" | "marketing";
  revocationToken: string | null;
  siteKey: string;
  sdk: { name: string; version: string };
  consent: ConsentSnapshot;
  policy: {
    region?: string;
    consentMode?: string;
    firstPartyCookies?: boolean;
    dataCategories: Record<string, boolean>;
  };
  visitor?: { anonId: string; sessionId: string };
  revocation?: {
    priorAnonId?: string;
    /** Dedicated consent-ordering capability; never copied into event props. */
    transitionToken?: string;
    /** Ordering time for the exact transition token revision. */
    occurredAt?: number;
  };
  context?: Record<string, unknown>;
};

export type QueuedEvent = {
  eventId: string;
  type: string;
  occurredAt: number;
  props?: Record<string, unknown>;
  enqueuedAt: number;
  attempts: number;
  /** Internal-only outbox identity; never mapped into the ingest payload. */
  consentEvidence?: ConsentEvidenceOutboxMarker;
  delivery?: QueuedDeliveryContext;
};

export type QueueConfig = {
  key: string;
  maxEvents: number;
  maxBytes: number;
  ttlMs: number;
};

/**
 * Consent evidence is the browser's durable denial outbox, not ordinary event
 * telemetry. Keep the newest unacknowledged revision for at least the server's
 * revocation-receipt horizon so an open, offline page cannot silently lose it
 * when the normal event TTL elapses.
 */
export const CONSENT_EVIDENCE_RETENTION_MS = 365 * 24 * 60 * 60 * 1000;

export type Queue = {
  /**
   * Enqueue for delivery and report whether the exact record survived a
   * storage readback plus queue compaction. A false result may still have an
   * in-page volatile fallback, but it is not recoverable after reload.
   */
  enqueue: (event: QueuedEvent) => { durable: boolean };
  someDurable: (predicate: (event: QueuedEvent) => boolean) => boolean;
  peek: () => QueuedEvent[];
  drain: () => QueuedEvent[];
  remove: (eventIds: Iterable<string>) => void;
  size: () => number;
  clear: () => void;
};

export function createQueue(
  storage: StorageAdapter,
  config: QueueConfig,
): Queue {
  type QueueEntry = {
    key: string;
    event: QueuedEvent;
    bytes: number;
    durable: boolean;
  };

  const eventPrefix = `${config.key}:event:`;
  const eventKey = (eventId: string) =>
    `${eventPrefix}${encodeURIComponent(eventId)}`;
  const volatile = new Map<string, QueuedEvent>();

  const isConsentEvidenceEvent = (event: QueuedEvent) =>
    event.type === "consent_update" && event.consentEvidence !== undefined;

  const isExpired = (event: QueuedEvent) => {
    const retentionMs = isConsentEvidenceEvent(event)
      ? Math.max(config.ttlMs, CONSENT_EVIDENCE_RETENTION_MS)
      : config.ttlMs;
    return now() - event.enqueuedAt > retentionMs;
  };

  const parseEvent = (raw: string | null): QueuedEvent | null => {
    if (!raw) return null;
    try {
      const event = JSON.parse(raw) as Partial<QueuedEvent>;
      if (
        typeof event.eventId !== "string" ||
        typeof event.type !== "string" ||
        typeof event.occurredAt !== "number" ||
        typeof event.enqueuedAt !== "number" ||
        typeof event.attempts !== "number"
      ) {
        return null;
      }
      return event as QueuedEvent;
    } catch {
      return null;
    }
  };

  const readEntries = () => {
    const entries = new Map<string, QueueEntry>();
    for (const key of storage.keys(eventPrefix)) {
      const raw = storage.get(key);
      const event = parseEvent(raw);
      if (!event || isExpired(event)) {
        storage.remove(key);
        continue;
      }
      entries.set(event.eventId, {
        key,
        event,
        bytes: raw?.length ?? 0,
        durable: true,
      });
    }
    for (const [eventId, event] of volatile) {
      if (isExpired(event)) {
        volatile.delete(eventId);
        continue;
      }
      let bytes = 0;
      try {
        bytes = JSON.stringify(event).length;
      } catch {
        volatile.delete(eventId);
        continue;
      }
      entries.set(eventId, {
        key: eventKey(eventId),
        event,
        bytes,
        durable: false,
      });
    }
    return Array.from(entries.values()).sort(
      (left, right) =>
        left.event.enqueuedAt - right.event.enqueuedAt ||
        left.event.eventId.localeCompare(right.event.eventId),
    );
  };

  const removeEntry = (entry: QueueEntry) => {
    storage.remove(entry.key);
    volatile.delete(entry.event.eventId);
  };

  const isConsentEvidence = (entry: QueueEntry) =>
    isConsentEvidenceEvent(entry.event);

  const removeExpiredRecords = () => {
    for (const key of storage.keys(eventPrefix)) {
      const event = parseEvent(storage.get(key));
      if (!event || isExpired(event)) {
        storage.remove(key);
      }
    }
  };

  const compact = () => {
    const entries = readEntries();
    const evidenceEntries = entries.filter(isConsentEvidence);
    const newestDurableEvidence = evidenceEntries
      .filter((entry) => entry.durable)
      .at(-1);
    const newestVolatileEvidence = evidenceEntries
      .filter((entry) => !entry.durable)
      .at(-1);
    const protectedEvidenceIds = new Set(
      [newestDurableEvidence, newestVolatileEvidence]
        .filter((entry): entry is QueueEntry => entry !== undefined)
        .map((entry) => entry.event.eventId),
    );

    // Keep at most one durable consent revision plus one in-page volatile
    // fallback. A newer durable revision safely supersedes older durable
    // evidence, while a failed write never deletes the last reload-safe denial.
    for (let index = entries.length - 1; index >= 0; index -= 1) {
      const entry = entries[index];
      if (
        entry &&
        isConsentEvidence(entry) &&
        !protectedEvidenceIds.has(entry.event.eventId)
      ) {
        entries.splice(index, 1);
        removeEntry(entry);
      }
    }

    let totalBytes = entries.reduce((sum, entry) => sum + entry.bytes, 0);
    while (
      entries.length > config.maxEvents ||
      (entries.length > 0 && totalBytes > config.maxBytes)
    ) {
      const removableIndex = entries.findIndex(
        (entry) => !protectedEvidenceIds.has(entry.event.eventId),
      );
      // One bounded outbox revision is reserved even when the host config sets
      // caps below its serialized size. Dropping it would let a stale server
      // grant survive while offline; later ordinary writes fail closed instead.
      if (removableIndex < 0) break;
      const [removed] = entries.splice(removableIndex, 1);
      if (!removed) break;
      totalBytes -= removed.bytes;
      removeEntry(removed);
    }
  };

  // Migrate the former single-array representation. Event-keyed records make
  // enqueue/remove operations independent across tabs, so one tab cannot
  // overwrite another tab's unacknowledged delivery attempt.
  const migrateLegacyQueue = () => {
    const raw = storage.get(config.key);
    if (!raw) return;
    let removeLegacy = true;
    try {
      const legacy = JSON.parse(raw) as unknown;
      if (Array.isArray(legacy)) {
        for (const candidate of legacy) {
          const event = parseEvent(JSON.stringify(candidate));
          if (!event || isExpired(event)) continue;
          // Event names in the former array format were caller-controlled, so
          // even essential-looking records are not trustworthy. Only records
          // carrying an immutable delivery/consent fence survive migration.
          if (!event.delivery) continue;
          const key = eventKey(event.eventId);
          const serialized = JSON.stringify(event);
          try {
            storage.set(key, serialized);
          } catch {
            volatile.set(event.eventId, event);
            removeLegacy = false;
            continue;
          }
          if (storage.get(key) !== serialized) {
            volatile.set(event.eventId, event);
            removeLegacy = false;
          }
        }
      }
    } catch {
      // Invalid legacy state is discarded below.
    }
    if (removeLegacy) storage.remove(config.key);
  };

  migrateLegacyQueue();
  removeExpiredRecords();
  compact();

  const enqueue = (event: QueuedEvent) => {
    const key = eventKey(event.eventId);
    let serialized: string | undefined;
    try {
      serialized = JSON.stringify(event);
      storage.set(key, serialized);
      if (storage.get(key) === serialized) {
        volatile.delete(event.eventId);
      } else {
        volatile.set(event.eventId, event);
      }
      compact();
    } catch {
      volatile.set(event.eventId, event);
      compact();
    }
    return {
      durable: serialized !== undefined && storage.get(key) === serialized,
    };
  };

  const someDurable = (predicate: (event: QueuedEvent) => boolean) => {
    for (const key of storage.keys(eventPrefix)) {
      const event = parseEvent(storage.get(key));
      if (!event || isExpired(event)) {
        storage.remove(key);
        continue;
      }
      if (predicate(event)) return true;
    }
    return false;
  };

  const peek = () => readEntries().map((entry) => entry.event);

  const drain = () => {
    const entries = readEntries();
    entries.forEach(removeEntry);
    return entries.map((entry) => entry.event);
  };

  const remove = (eventIds: Iterable<string>) => {
    const ids = new Set(eventIds);
    for (const key of storage.keys(eventPrefix)) {
      const event = parseEvent(storage.get(key));
      if (event && ids.has(event.eventId)) storage.remove(key);
    }
    for (const eventId of ids) {
      volatile.delete(eventId);
    }
  };

  const size = () => readEntries().length;

  const clear = () => {
    storage.keys(eventPrefix).forEach((key) => storage.remove(key));
    volatile.clear();
    storage.remove(config.key);
  };

  return { enqueue, someDurable, peek, drain, remove, size, clear };
}
